import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import { InspectionDeviationResultDto } from "@/types";
import Text from "@/components/atoms/Text";
import Textarea from "@/components/atoms/Textarea";
import SegmentedControls from "@/components/atoms/SegmentedControls";
import { FieldLabel } from "@/components/organisms/InspectionModal/components/FormParts";
import { useInspectionFormat } from "@/components/organisms/InspectionModal/useInspectionFormat";
import { Row } from "@/components/molecules/DrawerOrders/TabsContent/InspectionTab/TabParts";
import {
  DecisionChoice,
  DecisionErrors,
  DecisionValues,
  MAX_DECISION_COMMENT_LENGTH,
} from "./useDeviationDecisionsForm";
import { DecisionPreview } from "./useDecisionPreview";

// Pieces shared by the products and the services to decide.

/** A field's error as text. */
export const fieldError = (
  errors: DecisionErrors,
  deviation: InspectionDeviationResultDto,
  field: keyof DecisionValues,
  t: (key: string) => string
) => {
  const key = errors[`${deviation.id}.${field}`];
  return key ? t(key) : undefined;
};

/** The cost as the backend prices it: from the decision preview once it has priced the line, otherwise as saved. */
export const DecisionCost = ({
  deviation,
  preview,
}: {
  deviation: InspectionDeviationResultDto;
  preview: DecisionPreview;
}) => {
  const format = useInspectionFormat();
  const line = preview.preview?.deviations.find((item) => item.deviationId === deviation.id);

  return (
    <Text size="sm" data-testid={`deviation-decision-${deviation.id}-cost`}>
      {format.amount(line?.cost ?? deviation.cost, deviation.currencyType)}
    </Text>
  );
};

interface DecisionFieldsProps {
  deviation: InspectionDeviationResultDto;
  values: DecisionValues;
  onChange: (patch: Partial<DecisionValues>) => void;
  errors: DecisionErrors;
}

/** Confirm or reject a pending deviation; a rejection needs a comment. */
export const DecisionFields = ({ deviation, values, onChange, errors }: DecisionFieldsProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Row label={t("inspections:decisions.decision")}>
        <SegmentedControls
          aria-label={t("inspections:decisions.decision")}
          data={[
            { value: "CONFIRMED", label: t("inspections:decisions.confirm") },
            { value: "REJECTED", label: t("inspections:decisions.reject") },
          ]}
          // Nothing is picked until the manager decides.
          value={values.decision ?? ""}
          onChange={(decision) => onChange({ decision: decision as DecisionChoice })}
          data-testid={`deviation-decision-${deviation.id}-choice`}
        />
      </Row>
      {values.decision === "REJECTED" && (
        <>
          <Grid.Col span={3}>
            <FieldLabel required>{t("inspections:decisions.rejectComment")}</FieldLabel>
          </Grid.Col>
          <Grid.Col span={9}>
            <Textarea
              aria-label={t("inspections:decisions.rejectComment")}
              placeholder={t("inspections:decisions.rejectCommentPlaceholder")}
              rows={3}
              maxLength={MAX_DECISION_COMMENT_LENGTH}
              value={values.comment}
              onChange={(event) => onChange({ comment: event.currentTarget.value })}
              error={fieldError(errors, deviation, "comment", t)}
              data-testid={`deviation-decision-${deviation.id}-comment`}
            />
          </Grid.Col>
        </>
      )}
    </>
  );
};
